import React from 'react';

/**
 * Reusable Pagination component consistent with the application's design system.
 *
 * @param {Object} props
 * @param {number} props.currentPage - Currently active page (1-indexed)
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.totalCount - Total number of items across all pages
 * @param {function} props.onPageChange - Callback when page changes
 * @param {boolean} props.loading - Whether data is currently loading
 * @param {string} props.totalLabel - Label for the total count (e.g., "total orders")
 * @param {number} props.pageSize - Current page size
 * @param {function} props.onPageSizeChange - Callback when page size changes
 */
const Pagination = ({
    currentPage,
    totalPages,
    totalCount,
    onPageChange,
    loading = false,
    totalLabel = 'total entries',
    pageSize = 20,
    onPageSizeChange,
}) => {
    if (!totalCount && !loading) return null;

    return (
        <div
            className="mt-4 flex items-center justify-between rounded-xl border px-6 py-3"
            style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}
        >
            <div className="flex items-center gap-6">
                <span className="text-sm" style={{ color: '#6b7280' }}>
                    Page {currentPage} of {totalPages || 1} — {totalCount} {totalLabel}
                </span>

                {onPageSizeChange && (
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium uppercase tracking-wider" style={{ color: '#9ca3af' }}>
                            Show:
                        </span>
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            disabled={loading}
                            className="rounded-lg border bg-transparent px-2 py-1 text-sm font-medium transition-all focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                            style={{ borderColor: 'rgba(0,0,0,0.1)', color: '#374151' }}
                        >
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                )}
            </div>

            {totalPages > 1 && (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1 || loading}
                        className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                        style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6b7280' }}
                    >
                        Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                        .reduce((acc, p, idx, arr) => {
                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                            acc.push(p);
                            return acc;
                        }, [])
                        .map((item) =>
                            typeof item === 'string' ? (
                                <span key={item} className="px-2 text-sm" style={{ color: '#6b7280' }}>
                                    …
                                </span>
                            ) : (
                                <button
                                    key={item}
                                    type="button"
                                    onClick={() => onPageChange(item)}
                                    disabled={loading}
                                    className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed"
                                    style={{
                                        backgroundColor: item === currentPage ? '#0066cc' : 'transparent',
                                        color: item === currentPage ? '#fff' : '#374151',
                                        border: item === currentPage ? 'none' : '1px solid rgba(0,0,0,0.15)',
                                    }}
                                >
                                    {item}
                                </button>
                            )
                        )}
                    <button
                        type="button"
                        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage >= totalPages || loading}
                        className="rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                        style={{ backgroundColor: '#0066cc', color: '#fff' }}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default Pagination;
